import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";

const FormAbout = ({ titleAr, titleEn, currentData, onSubmit, isLoading }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const [titleArState, setTitleArState] = useState("");
    const [titleEnState, setTitleEnState] = useState("");
    const [subtitleArState, setSubtitleArState] = useState("");
    const [subtitleEnState, setSubtitleEnState] = useState("");
    const [descriptionArState, setDescriptionArState] = useState("");
    const [descriptionEnState, setDescriptionEnState] = useState("");
    const [yearsOfExperience, setYearsOfExperience] = useState("");
    const [happyClients, setHappyClients] = useState("");
    const [projectsCompleted, setProjectsCompleted] = useState("");
    const [image, setImage] = useState(null);

    useEffect(() => {
        if (currentData) {
            setTitleArState(currentData?.title?.ar || "");
            setTitleEnState(currentData?.title?.en || "");
            setSubtitleArState(currentData?.subtitle?.ar || "");
            setSubtitleEnState(currentData?.subtitle?.en || "");
            setDescriptionArState(currentData?.description?.ar || "");
            setDescriptionEnState(currentData?.description?.en || "");
            setYearsOfExperience(currentData?.years_of_experience || "");
            setHappyClients(currentData?.happy_clients || "");
            setProjectsCompleted(currentData?.projects_completed || "");
            setImage(currentData?.image || null);
        }
    }, [currentData]);

    const handleImageChange = (e) => {
        const val = e.target.value;
        setImage(val);
    };

    const getImageName = (imageVal) => {
        if (!imageVal) return "";
        if (typeof imageVal === 'string') {
            return imageVal.substr(imageVal.lastIndexOf('/') + 1);
        }
        return imageVal;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            title: {
                ar: titleArState || titleEnState,
                en: titleEnState || titleArState
            },
            subtitle: {
                ar: subtitleArState || subtitleEnState,
                en: subtitleEnState || subtitleArState
            },
            description: {
                ar: descriptionArState || descriptionEnState,
                en: descriptionEnState || descriptionArState
            },
            years_of_experience: yearsOfExperience,
            happy_clients: happyClients,
            projects_completed: projectsCompleted,
            image: getImageName(image)
        };

        onSubmit(payload);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isArabic ? titleAr : titleEn}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <FormInput
                            type="file"
                            name="image"
                            labelImage={isArabic ? "الصورة" : "Image"}
                            value={image}
                            onChange={handleImageChange}
                            width={200}
                            height={120}
                        />
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الرئيسي (عربي)"
                                placeholder="أدخل العنوان الرئيسي بالعربي"
                                value={titleArState}
                                onChange={(e) => setTitleArState(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Main Title (English)"
                                placeholder="Enter main title in English"
                                value={titleEnState}
                                onChange={(e) => setTitleEnState(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="العنوان الفرعي (عربي)"
                                placeholder="أدخل العنوان الفرعي بالعربي"
                                value={subtitleArState}
                                onChange={(e) => setSubtitleArState(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Subtitle (English)"
                                placeholder="Enter subtitle in English"
                                value={subtitleEnState}
                                onChange={(e) => setSubtitleEnState(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="number"
                            label={isArabic ? "سنوات الخبرة" : "Years of Experience"}
                            placeholder="e.g. 10"
                            value={yearsOfExperience}
                            onChange={(e) => setYearsOfExperience(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="number"
                            label={isArabic ? "العملاء المسرورون" : "Happy Clients"}
                            placeholder="e.g. 500"
                            value={happyClients}
                            onChange={(e) => setHappyClients(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="number"
                            label={isArabic ? "المشاريع المنجزة" : "Projects Completed"}
                            placeholder="e.g. 200"
                            value={projectsCompleted}
                            onChange={(e) => setProjectsCompleted(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                الوصف (عربي)
                            </Typography>
                            <TextEditor
                                name="description_ar"
                                value={descriptionArState}
                                onChange={(e) => setDescriptionArState(e.target.value)}
                                placeholder="أدخل الوصف بالعربي"
                            />
                        </Grid>
                    )}
                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Description (English)
                            </Typography>
                            <TextEditor
                                name="description_en"
                                value={descriptionEnState}
                                onChange={(e) => setDescriptionEnState(e.target.value)}
                                placeholder="Enter description in English"
                            />
                        </Grid>
                    )}
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        sx={{ minWidth: 120 }}
                    >
                        {isLoading ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormAbout;
